<template>
  <div
    class="tw-relative tw-text-dorian-gray-500 tw-w-full"
    ref="target"
    :class="className"
    data-test-id="testId"
  >
    <button
      @click="toggleIsOpen"
      class="tw-justify-between tw-items-center tw-w-full tw-py-2 tw-px-3 tw-flex tw-rounded tw-border tw-border-dorian-gray-100 tw-bg-white tw-text-dorian-gray-500 tw-outline-none tw-ring-offset-1 tw-transition-all focus:tw-border-secondary-500 focus:tw-ring-4 focus:tw-ring-secondary-500/10"
    >
      <div class="tw-flex" v-if="selectedValues?.length === 0">
        <div class="tw-flex tw-w-full tw-justify-between tw-items-center">
          <span class="tw-truncate">{{ placeholder }}</span>
        </div>
      </div>
      <span class="tw-truncate" v-else>
        {{ selectedValuesText }}
      </span>
      <ClIconTablerChevronDown
        v-if="selectedValues?.length === 0"
        class="tw-min-w-[16px] tw-transition-transform tw-text-dorian-gray-300"
        :width="'18px'"
        :height="'18px'"
        :class="chevronIconClass"
      />
      <ClIconTablerX
        v-else
        class="tw-min-w-[16px] tw-transition-transform tw-text-dorian-gray-300 tw-outline-none tw-ring-offset-1 focus:tw-border focus:tw-border-secondary-500 focus:tw-ring-4 focus:tw-ring-secondary-500/10"
        :width="'18px'"
        :height="'18px'"
        role="button"
        tabindex="0"
        @click.stop="clearSelected"
        @keydown.enter.stop="clearSelected"
        @keydown.space.stop.prevent="clearSelected"
      />
    </button>
    <div
      class="tw-bg-white tw-outline-t-0 tw-absolute tw-right-0 tw-z-10 tw-flex tw-w-full tw-flex-col tw-rounded-b-md tw-border tw-border-dorian-gray-100 tw-shadow tw-transition-opacity"
      v-if="isOpen"
    >
      <div class="tw-relative">
        <input
          type="text"
          v-model="searchTerm"
          class="tw-block tw-w-full tw-border tw-bg-white tw-text-dorian-gray-500 tw-outline-none tw-ring-offset-1 tw-transition-all tw-text-base tw-py-2 tw-pl-9 tw-pr-4 tw-border-dorian-gray-100/80 focus:tw-border-secondary-500 focus:tw-ring-4 focus:tw-ring-secondary-500/10 disabled:tw-cursor-not-allowed disabled:tw-bg-dorian-gray-50 disabled:tw-text-dorian-gray-500/50 disabled:tw-outline-none read-only:tw-cursor-default read-only:tw-bg-dorian-gray-50 read-only:tw-text-dorian-gray-500/50 read-only:tw-outline-none"
          placeholder="Search"
          @click.stop
          ref="searchInput"
        />

        <div
          class="tw-pointer-events-none tw-absolute tw-inset-y-0 tw-left-0 tw-flex tw-items-center tw-pl-3"
        >
          <ClIconTablerSearch
            :width="'16px'"
            :height="'16px'"
            class="tw-text-dorian-gray-200"
          />
        </div>
        <span
          v-if="searchTerm"
          class="tw-cursor-pointer tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-items-center tw-pr-3"
          role="button"
          @click="clearSearchTerm"
        >
          <ClIconTablerX
            :width="'16px'"
            :height="'16px'"
            class="tw-text-dorian-gray-200"
          />
        </span>
      </div>
      <ul ref="list" class="tw-max-h-40 tw-overflow-y-auto">
        <li
          v-for="option in displayedSelectedOptions"
          :key="option.value"
          class="tw-px-3 hover:tw-bg-secondary-50 tw-truncate"
        >
          <label
            class="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-py-2"
          >
            <ClInputCheckbox
              :id="option.value"
              :checked="displayedSelectedOptions?.includes(option)"
              :indeterminate="false"
              :name="option.text"
              @change="handleOptionClick(option)"
            />
            <span class="tw-truncate">{{ option.text }}</span>
          </label>
        </li>
        <li
          v-for="option in filteredOptions"
          :key="option.value"
          class="tw-px-3 hover:tw-bg-secondary-50 tw-truncate"
        >
          <label
            class="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-py-2"
          >
            <ClInputCheckbox
              :id="option.value"
              :checked="selectedValues?.some((v) => v.value === option.value)"
              :indeterminate="false"
              :name="option.text"
              @change="handleOptionClick(option)"
            />
            <span class="tw-truncate">{{ option.text }}</span>
          </label>
        </li>
        <li
          v-if="!displayedSelectedOptions.length && !filteredOptions.length"
          class="tw-py-2 tw-px-3"
        >
          {{ noResults }}
        </li>
        <li v-if="isFetching" class="tw-text-center tw-py-2">
          <ClSpinner :width="'20px'" :height="'20px'" />
        </li>
      </ul>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, computed, nextTick } from "vue";
import { onClickOutside, useInfiniteScroll } from "@vueuse/core";
import type { Option } from "./types";
import {
  ClIconTablerSearch,
  ClIconTablerChevronDown,
  ClIconTablerX,
} from "../icons";
import ClSpinner from "../spinner/cl-spinner.vue";
import ClInputCheckbox from "../input-checkbox/cl-input-checkbox.vue";

interface IProps {
  options: { text: string; value: string }[];
  isFetching: boolean;
  hasMore: boolean;
  placeholder: string;
  noResults: string;
  testId: string;
  className?: string;
}

const props = defineProps<IProps>();
const emit = defineEmits<{
  (event: "load-more"): void;
  (event: "update:values", value: Option[]): void;
  (event: "change", value: Option[]): void;
}>();

const target = ref(null);
const list = ref<HTMLElement | null>(null);
const searchInput = ref<HTMLElement | null>(null);
const isOpen = ref<boolean>(false);
const selectedValues = defineModel<Option[]>("values");
const searchTerm = defineModel<string>("search");

const toggleIsOpen = () => {
  isOpen.value = !isOpen.value;

  if (isOpen.value) {
    nextTick(() => {
      searchInput.value?.focus();
    });
  }
};

const chevronIconClass = computed(() =>
  isOpen.value ? "tw-rotate-180" : "tw-rotate-0",
);

onClickOutside(target, () => {
  if (isOpen.value) {
    isOpen.value = false;
  }
});

const selectedValuesText = computed<string>(() => {
  return selectedValues.value?.map((item) => item.text).join(", ") || "";
});

const displayedSelectedOptions = computed<{ text: string; value: string }[]>(
  () => {
    const selectedSet = new Set(
      selectedValues.value?.map((v) => v.value) ?? [],
    );

    return !searchTerm.value
      ? (selectedValues.value ?? [])
      : props.options.filter((option) => selectedSet.has(option.value));
  },
);

const handleOptionClick = (option: Option) => {
  const index =
    selectedValues.value?.findIndex((v) => v.value === option.value) ?? -1;
  if (index === -1) {
    selectedValues.value?.push(option);
  } else {
    selectedValues.value?.splice(index, 1);
  }
  emit("update:values", selectedValues.value ?? []);
  emit("change", selectedValues.value ?? []);
};

// Prevent from dispaying option when its selected
const filteredOptions = computed(() => {
  const selectedValuesSet = new Set(selectedValues.value?.map((v) => v.value));
  return props.options.filter((option) => !selectedValuesSet.has(option.value));
});

const clearSearchTerm = (): void => {
  searchTerm.value = "";
};

const clearSelected = (): void => {
  selectedValues.value = [];
  clearSearchTerm();
  emit("update:values", []);
  emit("change", []);
};

useInfiniteScroll(
  list,
  () => {
    emit("load-more");
  },
  {
    distance: 5,
    canLoadMore: () => {
      return props.hasMore;
    },
  },
);
</script>
